/**
 * Sentry — frontend (so erros).
 *
 * Sem REACT_APP_SENTRY_DSN -> no-op. Tracing/replay desligados.
 *
 * Setters expostos:
 *  - setSentryUser(email, tenantSlug) — chamado apos login
 *  - clearSentryUser() — chamado no logout
 *
 * Boundary: `SentryErrorBoundary` exportado a partir daqui (wrap em <App/>).
 */

import * as Sentry from '@sentry/react';
import React from 'react';
import { getDisplayMode } from './telemetry';

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_SENTRY_ENV || (process.env.NODE_ENV === 'production' ? 'production' : 'development');
const RELEASE = process.env.REACT_APP_RELEASE || undefined;

let initialized = false;

export function initSentry() {
  if (initialized) return;
  if (!DSN) {
    // eslint-disable-next-line no-console
    console.info('[Sentry] DSN nao definida — desativado');
    return;
  }
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    // So erros — sem performance, sem replay
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    // ignora ruidos comuns do navegador
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Network Error',           // axios offline
      'Request aborted',         // axios cancelToken
      'AbortError',
      // Google Translate / navegadores in-app mexem no DOM e quebram o React reconciler.
      // Nao e nosso codigo — nao ha fix limpo.
      "Failed to execute 'removeChild' on 'Node'",
      "Failed to execute 'insertBefore' on 'Node'",
      'NotFoundError: Failed to execute',
      // Instagram / Facebook in-app browser injeta handlers WebView que nao existem no web.
      "window.webkit.messageHandlers",
      "webkit.messageHandlers",
      // Ruidos de bots / extensions
      'top.GLOBALS',
      'ChunkLoadError',          // recarrega ja resolve; nao vale alertar
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-web-extension:\/\//i,
    ],
    beforeSend(event, hint) {
      // limpa qualquer Authorization que tenha vazado em breadcrumb
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
        delete event.request.headers.Cookie;
      }
      // Ignora erros em navegadores in-app (Instagram / Facebook / TikTok).
      // Sao WebViews que injetam handlers proprios e quebram scripts externos.
      const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
      if (/(FBAN|FBAV|Instagram|Twitter|TikTok|Line\/|MicroMessenger)/i.test(ua)) {
        return null;
      }
      // Ignora exceptions vindas de codigo que nao esta em nossos assets.
      // (frames sem stack ou com filenames externos que passaram pelos denyUrls)
      const frames = hint?.originalException?.stack || '';
      if (typeof frames === 'string' && /https?:\/\/[^/]+\/:(\d+):/.test(frames) && !frames.includes('/static/')) {
        // stack aponta pra codigo injetado inline no HTML (WebView)
        return null;
      }
      return event;
    },
  });

  Sentry.setTag('display_mode', getDisplayMode());
  // Expoe no window para debugging via console (DSN ja eh publica de qualquer
  // forma — visivel no bundle). Util para validar setup em prod.
  if (typeof window !== 'undefined') window.Sentry = Sentry;
  initialized = true;
}

export function setSentryUser(email, tenantSlug) {
  if (!initialized) return;
  if (email) Sentry.setUser({ email });
  if (tenantSlug) Sentry.setTag('tenant_slug', tenantSlug);
}

export function clearSentryUser() {
  if (!initialized) return;
  Sentry.setUser(null);
}

export function captureMessage(msg, level = 'info', extra = {}) {
  if (!initialized) return;
  Sentry.captureMessage(msg, { level, extra });
}

// Wrapper de ErrorBoundary com fallback minimalista.
export const SentryErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => (
      <div style={{
        padding: 24, maxWidth: 480, margin: '40px auto', textAlign: 'center',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <h2 style={{ color: '#C4786E', marginBottom: 12 }}>Algo deu errado</h2>
        <p style={{ color: '#6B5D5D', marginBottom: 20 }}>
          O problema foi registrado. Tente recarregar a tela.
        </p>
        <button onClick={resetError} style={{
          padding: '10px 18px', background: '#C4786E', color: '#fff',
          border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
        }}>
          Tentar novamente
        </button>
      </div>
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);
